import { toTask } from '@execonline-inc/maybe-adapter';
import { assertNever, noop } from '@kofno/piper';
import { useNavigate } from 'react-router-dom';
import { Task } from 'taskarian';
import { callApi } from '../../../../../../../Appy';
import { currentUserStore } from '../../../../../../../CurrentUserStore';
import { useFetchResource, useFindLinkByR } from '../../../../../../../Fetch';
import { declareEndpoint, findLinkByT } from '../../../../../../../Links';
import { Link } from '../../../../../../../Resource/Types';
import { createLearningCollectionResourceDecoder } from '../../Decoder';
import { learningCollectionEncoder } from '../../Encoders';
import { useSectionTypeOptions } from '../../hooks/useSectionTypeOptions';
import { useLearningCollection } from '../../LearningCollectionContext';
import { EditLearningCollectionResource, LearningCollectionResource } from '../../Types';
import SaveActionsStore from '../../ReadyBuilder/BuilderV2SaveActions/Store';
import { canAddBrowseAll, createBrowseAllSection } from '../../utils/browseAllSection';
import { createCuratedSections } from '../../utils/curatedSections';

export const useNewSectionsLoader = (store: SaveActionsStore) => {
  const { resourceState, setResourceState, logoChange } = useLearningCollection();
  const navigate = useNavigate();
  const { sectionTypeOptionsState } = useSectionTypeOptions();

  const link = useFindLinkByR(
    { rel: 'new-product-collection-template-v2', method: 'get' },
    currentUserStore.links,
  );

  const newProductCollectionState = useFetchResource(
    link.map(declareEndpoint('/new_product_collection_template')),
  );

  const canAdd = canAddBrowseAll(resourceState.payload.product_collections);

  const sendSaveNewRequest = (
    resource: LearningCollectionResource | EditLearningCollectionResource,
    isPublishing = false,
  ) => {
    if (isPublishing) {
      store.publishing();
    } else {
      store.saving();
    }

    Task.succeed<{}, ReadonlyArray<Link>>(currentUserStore.links)
      .andThen(findLinkByT({ rel: 'create-learning-collection-template-v2', method: 'post' }))
      .andThen(
        callApi(
          createLearningCollectionResourceDecoder,
          learningCollectionEncoder(resource, logoChange),
        ),
      )
      .andThen(({ payload }) => toTask({ kind: 'missing-id-error' }, payload.id))
      .fork(
        (err) => {
          store.error(err instanceof Error ? err : new Error('Failed to save collection'));
        },
        (id) => {
          store.ready();
          // Use setTimeout to allow React to complete state updates and modal cleanup
          // before navigation, preventing orphaned portal elements from blocking UI
          setTimeout(() => navigate('/learning_collections'), 0);
        },
      );
  };

  const handleNewProductCollectionChange = (insertionIndex?: number) => {
    switch (newProductCollectionState.kind) {
      case 'loading':
      case 'errored':
        return noop;
      case 'ready':
        const collections = [...resourceState.payload.product_collections];
        const newSection = {
          ...newProductCollectionState.data.resource,
          payload: {
            ...newProductCollectionState.data.resource.payload,
            name: 'Name your section',
          },
        };

        // If insertionIndex is provided, insert at that position; otherwise append to end
        if (insertionIndex !== undefined) {
          collections.splice(insertionIndex, 0, newSection);
        } else {
          collections.push(newSection);
        }

        const newResourceState = {
          ...resourceState,
          payload: {
            ...resourceState.payload,
            product_collections: collections,
          },
        };
        return setResourceState(newResourceState);
      default:
        assertNever(newProductCollectionState);
    }
  };

  // Note: scheduled_programs are automatically synced by LearningCollectionContext
  // when product_collections change, so we only need to update product_collections here
  const handleAddBrowseAllSection = (insertionIndex?: number) => {
    // Check if section type options are loaded
    if (sectionTypeOptionsState.status !== 'success' || !sectionTypeOptionsState.data) {
      console.error('Section type options not loaded');
      return;
    }

    const browseAllSection = createBrowseAllSection(
      sectionTypeOptionsState.data.payload.browse_all,
    );

    const collections = [...resourceState.payload.product_collections];

    // If insertionIndex is provided, insert at that position; otherwise append to end
    if (insertionIndex !== undefined) {
      collections.splice(insertionIndex, 0, browseAllSection);
    } else {
      collections.push(browseAllSection);
    }

    const newResourceState = {
      ...resourceState,
      payload: {
        ...resourceState.payload,
        product_collections: collections,
      },
    };

    setResourceState(newResourceState);
  };

  const handleAddCuratedSections = (curatedSectionIds: number[], insertionIndex?: number) => {
    // Check if section type options are loaded
    if (sectionTypeOptionsState.status !== 'success' || !sectionTypeOptionsState.data) {
      console.error('Section type options not loaded');
      return;
    }

    const curatedSections = createCuratedSections(
      curatedSectionIds,
      sectionTypeOptionsState.data.payload.curated,
    );

    const collections = [...resourceState.payload.product_collections];

    // If insertionIndex is provided, insert at that position; otherwise append to end
    if (insertionIndex !== undefined) {
      collections.splice(insertionIndex, 0, ...curatedSections);
    } else {
      collections.push(...curatedSections);
    }

    const newResourceState = {
      ...resourceState,
      payload: {
        ...resourceState.payload,
        product_collections: collections,
      },
    };

    setResourceState(newResourceState);
  };

  return {
    sendSaveNewRequest,
    handleNewProductCollectionChange,
    handleAddBrowseAllSection,
    handleAddCuratedSections,
    canAddBrowseAll: canAdd,
  };
};
